// ============================================================
// ProximasCompeticoes.js — a agenda do que ainda vai acontecer
//
// Aparece no fim da página de quem precisa saber quando são as
// competições: a coordenação, a equipe de cada modalidade e a preparação
// física, que decide a carga da semana a partir delas.
//
// São as MESMAS competições cadastradas em Competições e resultados. Não
// há segunda agenda a manter, e é isso que permite pôr a lista em três
// telas sem criar trabalho novo para ninguém.
//
// Quem vê o quê é decidido no servidor, e não aqui: a equipe de uma
// modalidade vê a dela, quem atende todas vê todas.
//
// A competição sai da lista no dia seguinte ao ÚLTIMO dia dela. Uma
// competição de três dias fica durante os três, porque quem abre o portal
// na manhã do segundo precisa vê-la ali.
// ============================================================

import { useState, useEffect } from "react";
import { listarProximasCompeticoes } from "../firebase";
import { normalizarModalidade } from "../calculo";
import Card from "./Card";
import { TEXTO, CINZA_ESC, BRANCO, BORDA, AZUL } from "../tema";

const DIGITO = "[0-9]";
const DATA_ISO = new RegExp("^" + DIGITO + "{4}-" + DIGITO + "{2}-" + DIGITO + "{2}$");

// Quantos dias faltam, contados só por data. Hora não entra: a competição
// é do dia inteiro, não do instante em que a tela abriu.
function diasAte(iso, hoje) {
  if (!iso || !hoje) return null;
  const [a1, m1, d1] = hoje.split("-").map(Number);
  const [a2, m2, d2] = iso.split("-").map(Number);
  return Math.round((Date.UTC(a2, m2 - 1, d2) - Date.UTC(a1, m1 - 1, d1)) / 86400000);
}

export default function ProximasCompeticoes({ onIr }) {
  const [dados, setDados] = useState(null);

  useEffect(() => {
    let vivo = true;
    listarProximasCompeticoes()
      .then((r) => { if (vivo) setDados(r || { competicoes: [], hojeISO: "" }); })
      // Falhar aqui não pode derrubar a tela: a agenda é apoio, e quem
      // precisa dela de verdade abre Competições e resultados.
      .catch(() => { if (vivo) setDados({ competicoes: [], hojeISO: "" }); });
    return () => { vivo = false; };
  }, []);

  if (!dados || !dados.competicoes.length) return null;
  const competicoes = dados.competicoes;
  const hoje = dados.hojeISO;

  // Sai quando o ÚLTIMO dia já passou: competição de três dias continua na
  // lista durante os três.
  const proximas = competicoes
    .filter((c) => String(c.dataFim || c.dataInicio || "") >= hoje)
    .sort((a, b) => String(a.dataInicio || "").localeCompare(String(b.dataInicio || "")));

  if (!proximas.length) return null;

  return (
    <Card
      titulo="Próximas competições"
      sub="Cadastradas em Competições e resultados. Saem desta lista no dia seguinte ao fim de cada uma."
      acao={onIr && (
        <button onClick={() => onIr("resultados")} style={{
          background: "none", border: "none", color: AZUL, fontSize: 12.5,
          fontWeight: 700, cursor: "pointer", fontFamily: "inherit",
        }}>
          Abrir competições →
        </button>
      )}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {proximas.map((c) => {
          const faltam = diasAte(c.dataInicio, hoje);
          const hojeMesmo = faltam !== null && faltam <= 0;
          return (
            <div key={c.id} style={{
              display: "flex", gap: 12, alignItems: "baseline", flexWrap: "wrap",
              border: `1px solid ${BORDA}`, borderRadius: 10, padding: "10px 13px",
              background: hojeMesmo ? "#EAF0FA" : BRANCO,
            }}>
              <div style={{ flex: 1, minWidth: 200 }}>
                <div style={{ fontSize: 13.5, fontWeight: 700, color: TEXTO }}>{c.nome}</div>
                <div style={{ fontSize: 12, color: CINZA_ESC, marginTop: 2 }}>
                  {normalizarModalidade(c.modalidade)}
                  {" · "}{periodoDaCompeticao(c)}
                  {c.local ? ` · ${c.local}` : ""}
                </div>
              </div>
              <span style={{
                fontSize: 11.5, fontWeight: 800, whiteSpace: "nowrap",
                color: hojeMesmo ? AZUL : CINZA_ESC,
              }}>
                {faltam === null ? ""
                  : faltam <= 0 ? "é hoje"
                  : faltam === 1 ? "amanhã"
                  : `em ${faltam} dias`}
              </span>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

function periodoDaCompeticao(c) {
  const br = (iso) => (DATA_ISO.test(String(iso || ""))
    ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}`
    : "");
  const inicio = br(c.dataInicio);
  const fim = br(c.dataFim);
  if (!fim || fim === inicio) return inicio;
  return inicio + " a " + fim;
}
